import { motion } from "framer-motion";
import React, { forwardRef, useCallback, useEffect, useRef, useState } from "react";
import Button from "src/components/UI/Button/Button";
import Card from "src/components/UI/Card/Card";
import DotPulseLoader from "src/components/UI/DotPulseLoader/DotPulseLoader";
import Slider from "src/components/UI/Slider/Slider";
import { FilteredLocationItems } from "src/components/features/search/SearchForm/elements/LocationItemGroup/FilteredLocationItemGroup";
import { TextInput } from "src/components/shared/input/FormInput/FormInputSimple";
import { useDeviceContext } from "src/context/device-context";
import { useLocaleContext } from "src/context/locale-context";
import useMousedownAway from "src/hooks/useMousedownAway";
import { airportsApi } from "src/services/api";
import { formatFlightDate } from "src/utils/cheap-flights";
import { userInputTag } from "../search/SearchForm/elements/AirportSelection/AirportSelection";
import { EmptyFilterResults } from "../search/SearchForm/elements/AirportSelection/EmptyFilterResults";

const DROPDOWN_STATE = {
  OPEN: "open",
  CLOSED: "close",
};

function FlightDealsFilterPanel({
  srcCityLocalised,
  destCityLocalised,
  priceEndRange,
  dateEndRange,
  setPriceRange,
  setDepartureDateRange,
  setLocation,
  tripType,
  setTripType,
  selectedDateRange,
  selectedPriceRange,
  setCommitState,
  expanded,
  setExpanded,
}) {
  const { isMobile } = useDeviceContext();
  const { getStringRes, currencySymbol, currentLocale } = useLocaleContext();
  const lang = currentLocale.split("_")[0].toUpperCase();

  const [sourceInput, setSourceInput] = useState(srcCityLocalised || "");
  const [destinationInput, setDestinationInput] = useState(destCityLocalised || "");
  const [dropdownData, setDropdownData] = useState({
    source: [],
    destination: [],
    loading: { source: false, destination: false },
  });
  const [dropdownState, setDropdownState] = useState({
    source: DROPDOWN_STATE.CLOSED,
    destination: DROPDOWN_STATE.CLOSED,
  });

  const srcRef = useRef(null);
  const destRef = useRef(null);

  const closeSrcDropdown = useCallback(
    () => setDropdownState((prev) => ({ ...prev, source: DROPDOWN_STATE.CLOSED })),
    []
  );
  const closeDestDropdown = useCallback(
    () => setDropdownState((prev) => ({ ...prev, destination: DROPDOWN_STATE.CLOSED })),
    []
  );

  useMousedownAway(srcRef, closeSrcDropdown);
  useMousedownAway(destRef, closeDestDropdown);

  const onPriceRangeChange = (values) => {
    const updValues = [
      values[0] < priceEndRange[0] ? priceEndRange[0] : values[0],
      values[1] > priceEndRange[1] ? priceEndRange[1] : values[1],
    ];
    if (updValues[0] === priceEndRange[0] && updValues[1] === priceEndRange[1]) {
      setPriceRange([undefined, undefined]);
    } else {
      setPriceRange(updValues);
    }
  };

  const onDateRangeChange = (values) => {
    const updValues = [
      values[0] < dateEndRange[0] ? dateEndRange[0] : values[0],
      values[1] > dateEndRange[1] ? dateEndRange[1] : values[1],
    ];
    if (updValues[0] === dateEndRange[0] && updValues[1] === dateEndRange[1]) {
      setDepartureDateRange([undefined, undefined]);
    } else {
      setDepartureDateRange(updValues);
    }
  };

  const onSelectLocation = (location, isCity = false, isOrigin) => {
    console.log(location, isCity, isOrigin);
    setLocation((prev) => ({
      ...prev,
      [isOrigin ? "origin" : "destination"]: location.cityIata,
    }));
    if (isOrigin) {
      setSourceInput(location.names[lang]);
    } else {
      setDestinationInput(location.names[lang]);
    }
    setDropdownState((prev) => ({
      ...prev,
      source: DROPDOWN_STATE.CLOSED,
      destination: DROPDOWN_STATE.CLOSED,
    }));
  };

  const onInputFocus = (type) => (e) => {
    const isSrc = type === "source";
    setExpanded((_) => true);
    setDropdownState((current) => ({
      ...current,
      [type]: DROPDOWN_STATE[e.target.value.length > 2 ? "OPEN" : "CLOSED"],
      [isSrc ? "destination" : "source"]: DROPDOWN_STATE.CLOSED,
    }));
  };

  const onInputChange = (type) => (e) => {
    if (type === "source") {
      setSourceInput(e.target.value);
    } else if (type === "destination") {
      setDestinationInput(e.target.value);
    }
    setDropdownState((current) => ({
      ...current,
      [type]: DROPDOWN_STATE[e.target.value.length > 2 ? "OPEN" : "CLOSED"],
    }));
  };

  useEffect(() => {
    console.log("useEffect");
    async function getSuggestions(inputQuery, lang) {
      return airportsApi
        .getAirports(`q=${inputQuery}`, lang)
        .then((res) => res.data)
        .catch((err) => {
          console.log(err);
          return [];
        });
    }

    let timer, inputToBeQueried;
    if (dropdownState.source === DROPDOWN_STATE.OPEN && sourceInput?.length > 2) {
      inputToBeQueried = "source";
    } else if (
      dropdownState.destination === DROPDOWN_STATE.OPEN &&
      destinationInput?.length > 2
    ) {
      inputToBeQueried = "destination";
    } else {
      setDropdownData((prev) => ({
        ...prev,
        source: [],
        destination: [],
        loading: { source: false, destination: false },
      }));
      if (!sourceInput) {
        setLocation((prev) => ({ ...prev, origin: undefined }));
      }
      if (!destinationInput) {
        setLocation((prev) => ({ ...prev, destination: undefined }));
      }
    }

    if (inputToBeQueried) {
      setDropdownData((prev) => ({
        ...prev,
        loading: { ...prev.loading, [inputToBeQueried]: true },
      }));
      timer = setTimeout(async () => {
        const suggestions = await getSuggestions(
          inputToBeQueried === "source" ? sourceInput : destinationInput,
          lang
        );
        setDropdownData((prev) => ({
          ...prev,
          [inputToBeQueried]: suggestions,
          loading: { ...prev.loading, [inputToBeQueried]: false },
        }));
      }, 500);
    }
    return () => {
      clearTimeout(timer);
      if (inputToBeQueried) {
        setDropdownData((prev) => ({
          ...prev,
          loading: { ...prev.loading, [inputToBeQueried]: false },
        }));
      }
    };
  }, [dropdownState, sourceInput, destinationInput, lang, setLocation]);

  const DropdownCard = forwardRef((props, ref) => (
    <Card
      ref={ref}
      cornerLg
      opts={
        props.data[props.inputType].length === 0 || props.data.loading[props.inputType]
          ? {
              maxWidth: "14rem",
              minWidth: "14rem",
              textAlign: props.data.loading[props.inputType] ? "center" : "left",
            }
          : {}
      }
      className="dropdown-suggestions">
      {props.data.loading[props.inputType] ? (
        <DotPulseLoader scale={0.9} />
      ) : props.data[props.inputType].length > 0 ? (
        <FilteredLocationItems
          items={props.data[props.inputType]}
          onSelect={(...args) => onSelectLocation(...args, props.inputType === "source")}
        />
      ) : (
        <EmptyFilterResults
          message={userInputTag(getStringRes("sf.result.empty"), props.input)}
        />
      )}
    </Card>
  ));

  return (
    <>
      <motion.ul className={`filters-panel ${expanded ? "expanded" : ""}`}>
        <li className="filter-section">
          <h3 className="filter-title">{getStringRes("cheap.flights.filter.title")}</h3>
          <div className="travel-location-container">
            <TextInput
              value={sourceInput}
              containerClass="travel-location"
              onChange={onInputChange("source")}
              onFocus={onInputFocus("source")}
              placeholder={getStringRes("cheap.flights.source.city")}
              required
            />
            {dropdownState.source === DROPDOWN_STATE.OPEN && (
              <DropdownCard
                data={dropdownData}
                inputType="source"
                ref={srcRef}
                input={sourceInput}
              />
            )}
          </div>
          <div className="travel-location-container">
            <TextInput
              value={destinationInput}
              containerClass="travel-location"
              onFocus={onInputFocus("destination")}
              onChange={onInputChange("destination")}
              placeholder={getStringRes("cheap.flights.destination.city")}
              required
            />
            {dropdownState.destination === DROPDOWN_STATE.OPEN && (
              <DropdownCard
                data={dropdownData}
                inputType="destination"
                input={destinationInput}
                ref={destRef}
              />
            )}
          </div>
        </li>

        <li className="filter-section">
          <h3 className="filter-title">{getStringRes("cheap.flights.trip.type.title")}</h3>
          <div className="trip-type-buttons">
            <Button
              name={getStringRes("cheap.flights.trip.type.one.way")}
              {...{ [tripType === "ow" ? "primary" : "secondary"]: true }}
              fontclass="trip-button"
              onClick={() => setTripType("ow")}
            />
            <Button
              name={getStringRes("cheap.flights.trip.type.round.trip")}
              fontclass="trip-button"
              {...{ [tripType === "rt" ? "primary" : "secondary"]: true }}
              onClick={() => setTripType("rt")}
            />
          </div>
        </li>

        <li className="filter-section">
          <h3 className="filter-title">
            {getStringRes("cheap.flights.filter.price.range.title")}
          </h3>
          <div className="value-slider">
            <p className="value-range">
              {selectedPriceRange[0] && selectedPriceRange[1]
                ? `${selectedPriceRange[0]}-${selectedPriceRange[1]} ${currencySymbol}`
                : getStringRes("res.filter.price.norange")}
            </p>
            <Slider
              current={
                selectedPriceRange[0] && selectedPriceRange[1]
                  ? selectedPriceRange
                  : priceEndRange
              }
              min={priceEndRange[0]}
              max={priceEndRange[1]}
              step={10}
              onBeforeChange={() => setCommitState((curr) => ({ ...curr, price: false }))}
              onAfterChange={() => setCommitState((curr) => ({ ...curr, price: true }))}
              onChange={onPriceRangeChange}
              minLabel={`${priceEndRange[0]} ${currencySymbol}`}
              maxLabel={`${priceEndRange[1]} ${currencySymbol}`}
              isRange
            />
          </div>
        </li>

        <li className="filter-section">
          <h3 className="filter-title">
            {getStringRes("cheap.flights.filter.date.range.title")}
          </h3>
          <div className="value-slider">
            <p className="value-range">
              {selectedDateRange[0] && selectedDateRange[1]
                ? `${formatFlightDate(selectedDateRange[0])}-${formatFlightDate(
                    selectedDateRange[1]
                  )}`
                : getStringRes("res.filter.time.norange")}
            </p>
            <Slider
              current={
                selectedDateRange[0] && selectedDateRange[1] ? selectedDateRange : dateEndRange
              }
              min={dateEndRange[0]}
              max={dateEndRange[1]}
              step={24 * 60 * 60} // 1 day
              onChange={onDateRangeChange}
              onAfterChange={() => setCommitState((curr) => ({ ...curr, date: true }))}
              onBeforeChange={() => setCommitState((curr) => ({ ...curr, date: false }))}
              minLabel={formatFlightDate(dateEndRange[0], lang)}
              maxLabel={formatFlightDate(dateEndRange[1], lang)}
              isRange
            />
          </div>
        </li>
      </motion.ul>
      {isMobile && (
        <button
          className="filters-visibility-switch"
          onClick={() => setExpanded((curr) => !curr)}>
          {getStringRes("res.filters.all.toggle." + (expanded ? "off" : "on"))}
        </button>
      )}
    </>
  );
}

export default FlightDealsFilterPanel;
